import { useEffect, useRef } from 'react';
import { ClapperboardIcon, PaperclipIcon } from 'lucide-react';

import { SUGGESTION_MODE_LABELS, type ComposerTab, type SuggestionMode } from '@/chat/types';
import {
  Attachment,
  AttachmentHoverCard,
  AttachmentHoverCardContent,
  AttachmentHoverCardTrigger,
  AttachmentInfo,
  AttachmentPreview,
  AttachmentRemove,
  Attachments,
} from '@/components/ai-elements/attachments';
import {
  PromptInputButton,
  PromptInputFooter,
  PromptInputHeader,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
  usePromptInputAttachments,
} from '@/components/ai-elements/prompt-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AudioTypeSelector } from '@/music/components/audio-type-selector';
import { MAX_AUDIO_ANALYSIS_DURATION, MAX_VIDEO_DURATION } from '@/upload/constants';
import { getMediaDuration } from '@/upload/media-duration';
import { NewChatButton } from './new-chat-button';

const TAB_OPTIONS: { value: ComposerTab; label: string }[] = [
  { value: 'suggest', label: '추천' },
  { value: 'replace', label: '교체' },
];

interface ComposerFieldsProps {
  tab: ComposerTab;
  onTabChange: (tab: ComposerTab) => void;
  hasActiveTurn: boolean;
  hasText: boolean;
  isStoryboard: boolean;
  mode: SuggestionMode;
  onAttached: () => void;
  onModeChange: (mode: SuggestionMode) => void;
  onNewChatConfirm: () => void;
  onNotice: (message: string | null) => void;
  onReject: (message: string) => void;
  onTextChange: (hasText: boolean) => void;
  onToggleStoryboard: () => void;
}

// usePromptInputAttachments는 PromptInput 내부에서만 쓸 수 있어 분리된 컴포넌트
export const ComposerFields = ({
  tab,
  onTabChange,
  hasActiveTurn,
  hasText,
  isStoryboard,
  mode,
  onAttached,
  onModeChange,
  onNewChatConfirm,
  onNotice,
  onReject,
  onTextChange,
  onToggleStoryboard,
}: ComposerFieldsProps) => {
  const attachments = usePromptInputAttachments();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hasAttachment = attachments.files.length > 0;
  const attachedFile = attachments.files[0] ?? null;
  const isImage = attachedFile?.mediaType?.startsWith('image/') ?? false;
  const isVideo = attachedFile?.mediaType?.startsWith('video/') ?? false;
  const isAudio = attachedFile?.mediaType?.startsWith('audio/') ?? false;

  useEffect(() => {
    if (hasAttachment) {
      onAttached();
    }
  }, [hasAttachment, onAttached]);

  // attachments.files의 항목 객체는 상위 리렌더마다 재생성되므로 원시값과
  // 안정적인 remove 콜백에만 의존 (객체 의존 시 안내 설정 -> 리렌더 -> 재실행 루프)
  const attachedId = attachedFile?.id ?? null;
  const attachedUrl = attachedFile?.url ?? null;
  const removeAttachment = attachments.remove;

  useEffect(() => {
    if (!attachedId || !attachedUrl || (!isVideo && !isAudio)) return;

    let cancelled = false;
    void getMediaDuration(attachedUrl).then((duration) => {
      if (cancelled || duration === null) return;
      if (isVideo && duration > MAX_VIDEO_DURATION) {
        removeAttachment(attachedId);
        onReject('3분 이하의 영상만 첨부할 수 있습니다');
      }
      if (isAudio && duration > MAX_AUDIO_ANALYSIS_DURATION) {
        if (tab === 'replace') {
          removeAttachment(attachedId);
          onReject('5분 이하의 음원만 교체할 수 있습니다');
        } else {
          onNotice('5분이 넘는 음원은 앞 5분까지만 분석에 사용돼요');
        }
      }
    });

    return () => {
      cancelled = true;
      onNotice(null);
    };
  }, [attachedId, attachedUrl, isVideo, isAudio, tab, removeAttachment, onNotice, onReject]);

  const handleTabChange = (newTab: ComposerTab) => {
    if (newTab === tab) return;
    attachments.clear();
    // textarea는 비제어 폼 필드라 상태 초기화만으로는 값이 남으므로 직접 비움
    if (textareaRef.current) {
      textareaRef.current.value = '';
    }
    onTextChange(false);
    onTabChange(newTab);
  };

  const submitDisabled =
    tab === 'replace'
      ? !hasAttachment || hasActiveTurn
      : (!hasText && !hasAttachment) || hasActiveTurn;

  return (
    <>
      {hasAttachment && (
        <PromptInputHeader>
          <Attachments variant="inline">
            {attachments.files.map((file) => (
              <AttachmentHoverCard key={file.id}>
                <AttachmentHoverCardTrigger asChild>
                  <Attachment data={file} onRemove={() => attachments.remove(file.id)}>
                    <AttachmentPreview />
                    <AttachmentInfo />
                    <AttachmentRemove />
                  </Attachment>
                </AttachmentHoverCardTrigger>
                <AttachmentHoverCardContent>
                  {file.mediaType?.startsWith('audio/') ? (
                    <audio controls src={file.url} />
                  ) : file.mediaType?.startsWith('video/') ? (
                    <video className="max-h-64 rounded-md" muted src={file.url} />
                  ) : (
                    <img
                      alt={file.filename ?? '첨부 이미지'}
                      className="max-h-64 rounded-md"
                      src={file.url}
                    />
                  )}
                </AttachmentHoverCardContent>
              </AttachmentHoverCard>
            ))}
          </Attachments>
        </PromptInputHeader>
      )}
      <PromptInputTextarea
        ref={textareaRef}
        className="md:text-base"
        onChange={(e) => onTextChange(e.currentTarget.value.trim().length > 0)}
        placeholder={tab === 'replace' ? '어떤 느낌으로 바꿀까요?' : '어떤 음악이 필요하세요?'}
      />
      <PromptInputFooter>
        <PromptInputTools>
          <PromptInputButton
            onClick={attachments.openFileDialog}
            tooltip={
              tab === 'replace'
                ? '음원 첨부 (최대 100MB, 5분 이하)'
                : '이미지, 영상 또는 음원 첨부 (최대 100MB, 영상 3분 이하)'
            }
          >
            <PaperclipIcon className="size-4" />
          </PromptInputButton>
          <AudioTypeSelector value={tab} onChange={handleTabChange} options={TAB_OPTIONS} size="md" />
          {tab === 'suggest' && (
            <>
              <Select value={mode} onValueChange={(v) => onModeChange(v as SuggestionMode)}>
                <SelectTrigger className="h-8 w-auto gap-1.5 border-none bg-transparent px-2.5 text-sm shadow-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.entries(SUGGESTION_MODE_LABELS) as [SuggestionMode, string][]).map(
                    ([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
              {isImage && (
                <PromptInputButton
                  aria-pressed={isStoryboard}
                  className={isStoryboard ? 'border border-transparent' : undefined}
                  onClick={onToggleStoryboard}
                  variant={isStoryboard ? 'default' : 'outline'}
                >
                  <ClapperboardIcon className="size-4" />
                  스토리보드
                </PromptInputButton>
              )}
            </>
          )}
          <NewChatButton
            onConfirm={() => {
              attachments.clear();
              onNewChatConfirm();
            }}
          />
        </PromptInputTools>
        <PromptInputSubmit
          disabled={submitDisabled}
          status={hasActiveTurn ? 'submitted' : undefined}
        />
      </PromptInputFooter>
    </>
  );
};
